import React, { useState, useEffect, useRef } from 'react';
import DashboardTable from '../DashboardTable/DashboardTable'
import styles from './DashboardPage.module.css'
import EditNoteIcon from '@mui/icons-material/EditNote';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DashboardConfiguration from '../DashboardConfiguration/DashboardConfiguration';
import RadioGroupWithDelete from '../RadioGroupwithDelete/RadioGroupwithDelete';
import EditableField from '../EditableField/EditableField';
import AssignApproverModal from '../AssignApproverModal/AssignApproverModal';
import useDashboardQuery from '../../hooks/useDashboardQuery';
import useProfileQuery from '../../hooks/useProfileQuery';
import useAssignApprover from '../../hooks/useAssignApprover';
import {
    GET_MY_SETTINGS,
    GET_NOTIFICATIONS,
    MARK_NOTIFICATION_READ,
} from '../../services/graphql';
import { useMutation, useQuery } from '@apollo/client';
import keycloak from '../../keycloak';
import { useSelector } from 'react-redux';

const baseColumns = [
    'date',
    'invoiceID',
    'supplierEmail',
    'expenseType',
    'category',
    'originalAmount',
    'status',
    'paidDate',
    'approverEmails',
    'GST',
    'PST',
    'distance',
    'project',
    'entity',
    'allocation'
];

const managerApproverExtraColumns = [
    'GIFICode',
    'fiscalYear',
    'backOfficeNote',
    'expenseGroup',
    'capital',
];

const columnsMap = {
    'date': 'Date',
    'invoiceID': '#',
    'supplierEmail': 'Submitted By',
    'expenseType': 'Expense Type',
    'category': 'Category',
    'originalAmount': 'Sub Total',
    'status': 'Status',
    'paidDate': 'Remittance Date',
    'approverEmails': 'Assigned To',
    'GST': 'GST',
    'PST': 'PST',
    'distance': 'Distance',
    'project': 'Project',
    'entity': 'Entity',
    'allocation': 'Allocation',
    'GIFICode': 'GIFI Code',
    'fiscalYear': 'Fiscal Year',
    'backOfficeNote': 'Back Office Note',
    'expenseGroup': 'Expense Group',
    'capital': 'Capital',
};

export default function DashboardPage() {
    const [isModalOpen, setIsModalOpen] = useState(false);

    const [selectedColumns, setSelectedColumns] = useState(baseColumns);
    const [draftSelectedColumns, setDraftSelectedColumns] = useState(baseColumns);
    const [viewBy, setViewBy] = useState('currentPeriod'); //this will be sent to backend for filtering invoices
    const [showColumnFilters, setShowColumnFilters] = useState(false);
    const [showSearchInput, setShowSearchInput] = useState(false);
    const [showColumnPicker, setShowColumnPicker] = useState(false);
    const [globalFilter, setGlobalFilter] = useState('');
    const [showAdminNotifications, setShowAdminNotifications] = useState(false);
    const [showUserNotifications, setShowUserNotifications] = useState(false);
    const [isDismissingByType, setIsDismissingByType] = useState({ admin: false, user: false });
    const [tableRefreshSignal, setTableRefreshSignal] = useState(0);
    const searchInputRef = useRef(null);
    const columnPickerRef = useRef(null);
    const [dashboardColumnOrder, setDashboardColumnOrder] = useState(baseColumns);
    const [draggedColumnIndex, setDraggedColumnIndex] = useState(null);

    //clicking on a row opens a page with the invoice details (not sure how the images are handled yet with backend)
    const [selectedConfigOption, setSelectedConfigOption] = useState('List All');
    const [draftSelectedConfigOption, setDraftSelectedConfigOption] = useState('List All');
    const [configName, setConfigName] = useState('');

    const {data, refetch} = useDashboardQuery();
    const {updateMyProfile} = useProfileQuery();
    const email = keycloak.tokenParsed?.email;
    const profileNotFound = Boolean(data && data.getUserProfile && !data.getUserProfile.body);
    const userType = useSelector(state => state.graphql.accessLevel);
    const isManager = userType === 'Manager';
    const isApprover = userType?.startsWith('Approver');
    const canSeeManagerApproverColumns = isManager || isApprover;
    const availableColumns = canSeeManagerApproverColumns
        ? [...baseColumns, ...managerApproverExtraColumns]
        : baseColumns;

    const [configOptions, setConfigOptions] = useState([]);
    const [configColumnMapping, setConfigColumnMapping] = useState({'List All': baseColumns});

    useQuery(GET_MY_SETTINGS, {
        variables: { email: keycloak.tokenParsed?.email },
        onCompleted(data){
          const userSettings = data.getUserProfile.body;
          if (!userSettings) return;
          localStorage.setItem("tableDensity", userSettings.tableDensity);
    }});

    const {
        data: notificationsData,
        error: notificationsError,
        refetch: refetchNotifications
    } = useQuery(
        GET_NOTIFICATIONS,
        {
            variables: { unreadOnly: true },
            fetchPolicy: 'network-only',
        }
    );

    const [markNotificationRead] = useMutation(MARK_NOTIFICATION_READ);
    const {
        assigningInvoiceID,
        selectedApproverEmail,
        setSelectedApproverEmail,
        availableApprovers,
        assignApproverLoading,
        approversLoading,
        openAssignApproverModal,
        closeAssignApproverModal,
        assignSelectedApprover,
    } = useAssignApprover({ enabled: isManager });

    const unreadNotifications = notificationsData?.getNotifications?.body || [];
    const adminNotifications = unreadNotifications.filter((notice) => notice.recipientRole === 'Manager');
    const userNotifications = unreadNotifications.filter((notice) => notice.recipientEmail);
    const isDismissingAdmin = isDismissingByType.admin;
    const isDismissingUser = isDismissingByType.user;

    const markNotificationsRead = async (notificationIds) => {
        if (!Array.isArray(notificationIds) || notificationIds.length === 0) return;
        await Promise.all(
            notificationIds.map((id) => markNotificationRead({ variables: { id } }))
        );
    };

    const handleDismissNotification = async (id) => {
        try {
            await markNotificationsRead([id]);
            await refetchNotifications();
        } catch (error) {
            console.error('Failed to dismiss notification:', error);
        }
    };

    const handleDismissAllNotifications = async (type, notifications) => {
        if (isDismissingByType[type] || notifications.length === 0) return;
        setIsDismissingByType((prev) => ({ ...prev, [type]: true }));
        try {
            await markNotificationsRead(notifications.map((notice) => notice.id));
            await refetchNotifications();
        } catch (error) {
            console.error(`Failed to dismiss all ${type} notifications:`, error);
        } finally {
            setIsDismissingByType((prev) => ({ ...prev, [type]: false }));
        }
    };

    const handleDismissAllAdminNotifications = async () => {
        await handleDismissAllNotifications('admin', adminNotifications);
    };

    const handleDismissAllUserNotifications = async () => {
        await handleDismissAllNotifications('user', userNotifications);
    };

    useEffect(() => {
        if (notificationsError) {
            console.error('Notifications query error:', notificationsError);
        }
    }, [notificationsError]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (columnPickerRef.current && !columnPickerRef.current.contains(event.target)) {
                setShowColumnPicker(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);


    const handleSelectConfigOption = (option) => {
        setDraftSelectedConfigOption(option);
        setDraftSelectedColumns(configColumnMapping[option] || []);
    };

    const handleCheckboxChange = (value) => {
        if (draftSelectedColumns.includes(value)) {
            setDraftSelectedColumns(draftSelectedColumns.filter((checkbox) => checkbox !== value));
        } else {
            setDraftSelectedColumns([...draftSelectedColumns, value]);
        }
    };

    const handleViewByChange = (event) => {
        setViewBy(event.target.value);
    };

    const openModal = () => {
        setDraftSelectedColumns(selectedColumns);
        setDraftSelectedConfigOption(selectedConfigOption);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    useEffect(() => {
        if (data && data.getUserProfile && data.getUserProfile.body) {
            const userConfig = data.getUserProfile.body;

            if (userConfig.dashboardCfg !== undefined && userConfig.dashboardCfg !== null && userConfig.dashboardCfg !== 'NULL') {

                if (userConfig.dashboardCfg !== "InvoiceID"){
                    let parsedDashCfg = JSON.parse(userConfig.dashboardCfg);
                    setConfigColumnMapping(parsedDashCfg);

                    setConfigOptions(Object.keys(parsedDashCfg));
                }

            }
        }
    }, [data]);

    useEffect(() => {
        if (canSeeManagerApproverColumns) {
            setSelectedColumns((prev) => [...new Set([...prev, ...managerApproverExtraColumns])]);
            setDraftSelectedColumns((prev) => [...new Set([...prev, ...managerApproverExtraColumns])]);
            setDashboardColumnOrder((prev) => [...new Set([...prev, ...managerApproverExtraColumns])]);
            setConfigColumnMapping((prev) => {
                const listAll = prev['List All'] || baseColumns;
                return {
                    ...prev,
                    'List All': [...new Set([...listAll, ...managerApproverExtraColumns])],
                };
            });
            return;
        }

        setSelectedColumns((prev) => prev.filter((col) => !managerApproverExtraColumns.includes(col)));
        setDraftSelectedColumns((prev) => prev.filter((col) => !managerApproverExtraColumns.includes(col)));
        setDashboardColumnOrder((prev) => prev.filter((col) => !managerApproverExtraColumns.includes(col)));
        setConfigColumnMapping((prev) => {
            const next = {};
            Object.keys(prev).forEach((key) => {
                next[key] = (prev[key] || []).filter((col) => !managerApproverExtraColumns.includes(col));
            });
            return next;
        });
    }, [canSeeManagerApproverColumns]);

    const handleSave = async() => {
        const typedName = configName.trim();
        // No new name typed while an existing (non-default) config is selected: update it in place.
        const isEditingExisting = !typedName && draftSelectedConfigOption && draftSelectedConfigOption !== 'List All';
        const targetName = isEditingExisting ? draftSelectedConfigOption : typedName;

        if (!targetName) {
            alert('Please enter a configuration name before saving.');
            return;
        }
        const updatedConfigOptions = configOptions.includes(targetName)
            ? configOptions
            : [...configOptions, targetName];
        const updatedMapping = { ...configColumnMapping, [targetName]: draftSelectedColumns };

        setConfigOptions(updatedConfigOptions);
        setConfigColumnMapping(updatedMapping);
        setDraftSelectedConfigOption(targetName);
        setConfigName('');
        
        let changes = {"dashboardCfg": JSON.stringify(updatedMapping)};
        try {
            await updateMyProfile(email, changes);
            refetch();
        } catch (error) {
            console.error("Error updating dashboard configuration:", error);
        }

    };

    const handleApply = () => {
        setSelectedColumns(draftSelectedColumns);
        setSelectedConfigOption(draftSelectedConfigOption);
        closeModal();
        setConfigName('');
    };

    const handleconfigChange = (value) => {
        setConfigName(value || '');
    };

    const deleteConfigOption = async (optionToDelete) => {
        const updatedConfigOptions = configOptions.filter(option => option !== optionToDelete);
        const updatedMapping = {};

        Object.keys(configColumnMapping).forEach(option => {
            if (option !== optionToDelete) {
                updatedMapping[option] = configColumnMapping[option];
            }
        });

        setConfigOptions(updatedConfigOptions);
        setConfigColumnMapping(updatedMapping);

        let changes = {"dashboardCfg": JSON.stringify(updatedMapping)};
        try {
            await updateMyProfile(email, changes);
            refetch();
        } catch (error) {
            console.error("Error updating dashboard configuration:", error);
        }

    };

    const displayedColumnOrder = dashboardColumnOrder.filter((key) => selectedColumns.includes(key) && availableColumns.includes(key));

    const moveDisplayedColumn = (fromIndex, toIndex) => {
        if (fromIndex === null || toIndex === null || fromIndex === toIndex) return;
        if (toIndex < 0 || toIndex >= displayedColumnOrder.length) return;
        const visible = [...displayedColumnOrder];
        const [moved] = visible.splice(fromIndex, 1);
        visible.splice(toIndex, 0, moved);
        const hidden = dashboardColumnOrder.filter((key) => !selectedColumns.includes(key) || !availableColumns.includes(key));
        setDashboardColumnOrder([...visible, ...hidden]);
    };

    const handleAssignFromNotification = async () => {
        await assignSelectedApprover(async () => {
            await refetchNotifications();
            setTableRefreshSignal((prev) => prev + 1);
        });
    };

    if (profileNotFound) {
        return (
            <div className={styles.profileMissing}>
                <p>We couldn't find your profile. If your account was just created, this can take a moment — try refreshing. If it persists, contact your administrator.</p>
            </div>
        );
    }

    return (
        <div className={styles.pageContainer}>
            {isManager && adminNotifications.length > 0 && (
                <div className={styles.adminNotice}>
                    <div className={`${styles.noticeHeaderRow} ${styles.adminNoticeHeader}`}>
                        <button
                            type="button"
                            className={styles.noticeHeaderButton}
                            onClick={() => setShowAdminNotifications((prev) => !prev)}
                        >
                            <div className={styles.noticeHeaderMeta}>
                                <span className={styles.adminNoticeTitle}>Requires Review</span>
                                <span className={styles.adminNoticeCount}>{adminNotifications.length}</span>
                            </div>
                        </button>
                        <div className={styles.noticeHeaderActions}>
                            {showAdminNotifications && (
                                <button
                                    type="button"
                                    className={styles.noticeDismissAll}
                                    onClick={handleDismissAllAdminNotifications}
                                    disabled={isDismissingAdmin}
                                >
                                    {isDismissingAdmin ? 'Dismissing...' : 'Dismiss All'}
                                </button>
                            )}
                            <button
                                type="button"
                                className={styles.noticeToggleButton}
                                onClick={() => setShowAdminNotifications((prev) => !prev)}
                                aria-label={showAdminNotifications ? 'Collapse notifications' : 'Expand notifications'}
                            >
                                {showAdminNotifications ? (
                                    <KeyboardArrowDownIcon className={styles.noticeToggleIcon} />
                                ) : (
                                    <KeyboardArrowRightIcon className={styles.noticeToggleIcon} />
                                )}
                            </button>
                        </div>
                    </div>
                    {showAdminNotifications && (
                        <div className={styles.adminNoticeList}>
                            <div className={styles.noticeListScroll}>
                                {adminNotifications.map((notice) => (
                                    <div key={notice.id} className={styles.adminNoticeItem}>
                                        <div className={styles.adminNoticeMessage}>{notice.message}</div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            {notice.type === 'NoApproverAvailable' && notice.invoiceID ? (
                                                <button
                                                    className={styles.adminNoticeDismiss}
                                                    onClick={() => openAssignApproverModal(notice.invoiceID)}
                                                >
                                                    Assign Approver
                                                </button>
                                            ) : null}
                                            <button
                                                className={styles.adminNoticeDismiss}
                                                onClick={() => handleDismissNotification(notice.id)}
                                            >
                                                Dismiss
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
            {userNotifications.length > 0 && (
                <div className={styles.userNotice}>
                    <div className={`${styles.noticeHeaderRow} ${styles.userNoticeHeader}`}>
                        <button
                            type="button"
                            className={styles.noticeHeaderButton}
                            onClick={() => setShowUserNotifications((prev) => !prev)}
                        >
                            <div className={styles.noticeHeaderMeta}>
                                <span className={styles.userNoticeTitle}> Updates</span>
                                <span className={styles.userNoticeCount}>{userNotifications.length}</span>
                            </div>
                        </button>
                        <div className={styles.noticeHeaderActions}>
                            {showUserNotifications && (
                                <button
                                    type="button"
                                    className={styles.noticeDismissAll}
                                    onClick={handleDismissAllUserNotifications}
                                    disabled={isDismissingUser}
                                >
                                    {isDismissingUser ? 'Dismissing...' : 'Dismiss All'}
                                </button>
                            )}
                            <button
                                type="button"
                                className={styles.noticeToggleButton}
                                onClick={() => setShowUserNotifications((prev) => !prev)}
                                aria-label={showUserNotifications ? 'Collapse notifications' : 'Expand notifications'}
                            >
                                {showUserNotifications ? (
                                    <KeyboardArrowDownIcon className={styles.noticeToggleIcon} />
                                ) : (
                                    <KeyboardArrowRightIcon className={styles.noticeToggleIcon} />
                                )}
                            </button>
                        </div>
                    </div>
                    {showUserNotifications && (
                        <div className={styles.userNoticeList}>
                            <div className={styles.noticeListScroll}>
                                {userNotifications.map((notice) => (
                                    <div key={notice.id} className={styles.userNoticeItem}>
                                        <div className={styles.userNoticeMessage}>{notice.message}</div>
                                        <button
                                            className={styles.userNoticeDismiss}
                                            onClick={() => handleDismissNotification(notice.id)}
                                        >
                                            Dismiss
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
            <h2 className={styles.title}>Invoice History</h2>
            <div className={styles.buttonContainer}>
                <div className={styles.dropdownContainer}>
                    <select id="dropdown" className={styles.dropdown} value={viewBy} onChange={handleViewByChange}>
                        <option value="currentPeriod">View By: Current Period</option>
                        <option value="currentMonth">View By: Current Month</option>
                        <option value="currentYear">View By: Current Year</option>
                        <option value="all">View By: All</option>
                    </select>
                </div>
                <button
                    className={styles.externalControlButton}
                    onClick={() => setShowColumnFilters((prev) => !prev)}
                >
                    <TuneIcon fontSize="small" />
                    {showColumnFilters ? 'Hide Filters' : 'Show Filters'}
                </button>
                {showSearchInput ? (
                    <input
                        ref={searchInputRef}
                        className={styles.searchInlineInput}
                        type="text"
                        placeholder="Search invoices..."
                        value={globalFilter || ''}
                        onChange={(e) => setGlobalFilter(e.target.value)}
                        onBlur={() => {
                            if (!globalFilter) setShowSearchInput(false);
                        }}
                    />
                ) : (
                    <button
                        className={styles.externalControlButton}
                        onClick={() => {
                            setShowSearchInput(true);
                            setTimeout(() => searchInputRef.current?.focus(), 0);
                        }}
                    >
                        <SearchIcon fontSize="small" />
                        Search
                    </button>
                )}
                <div className={styles.columnPickerAnchor} ref={columnPickerRef}>
                    <button
                        className={styles.externalControlButton}
                        onClick={() => setShowColumnPicker((prev) => !prev)}
                    >
                        Columns
                    </button>
                    {showColumnPicker && (
                        <div className={styles.columnPicker}>
                            <button
                                type="button"
                                className={styles.resetOrderButton}
                                onClick={() => setDashboardColumnOrder(availableColumns)}
                            >
                                Reset Order
                            </button>
                            <div className={styles.columnList}>
                                {displayedColumnOrder.map((key, index) => (
                                    <div
                                        key={key}
                                        className={styles.columnOption}
                                        draggable
                                        onDragStart={() => setDraggedColumnIndex(index)}
                                        onDragOver={(event) => event.preventDefault()}
                                        onDrop={() => {
                                            moveDisplayedColumn(draggedColumnIndex, index);
                                            setDraggedColumnIndex(null);
                                        }}
                                        onDragEnd={() => setDraggedColumnIndex(null)}
                                    >
                                        <div className={styles.columnLabelWrap}>
                                            <DragIndicatorIcon className={styles.dragHandle} />
                                            <span className={styles.columnName}>{columnsMap[key] || key}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                <div className={styles.iconContainer}>
                    <EditNoteIcon className={styles.editIcon} fontSize='large' onClick={openModal} />
                </div>
                <div id="dashboard-edit-controls-slot" className={styles.inlineEditControls} />
            </div>
            <DashboardTable
                selectedColumns={selectedColumns}
                viewBy={viewBy}
                showColumnFilters={showColumnFilters}
                globalFilter={globalFilter}
                onGlobalFilterChange={setGlobalFilter}
                columnOrder={dashboardColumnOrder}
                editControlsTargetId="dashboard-edit-controls-slot"
                controlButtonClassName={styles.externalControlButton}
                cancelButtonClassName={`${styles.externalControlButton} ${styles.cancelControlButton}`}
                controlMetaClassName={styles.editControlMeta}
                refreshSignal={tableRefreshSignal}
            ></DashboardTable>
            <AssignApproverModal
                isOpen={Boolean(assigningInvoiceID)}
                invoiceID={assigningInvoiceID}
                selectedApproverEmail={selectedApproverEmail}
                onApproverChange={setSelectedApproverEmail}
                availableApprovers={availableApprovers}
                onCancel={closeAssignApproverModal}
                onAssign={handleAssignFromNotification}
                isAssigning={assignApproverLoading}
                isApproversLoading={approversLoading}
                cancelButtonClassName={styles.noticeDismissAll}
                assignButtonClassName={styles.noticeDismissAll}
            />
            {isModalOpen && (
                <div className={styles.modalContainer}>
                    <DashboardConfiguration title="Dashboard Configuration" onClose={closeModal}>
                        <div>
                            Select existing configuration:
                            <div className={styles.selectConfig}>
                                <RadioGroupWithDelete
                                    options={configOptions}
                                    selectedOption={draftSelectedConfigOption}
                                    onChange={handleSelectConfigOption}
                                    handleDeletion={(option) => deleteConfigOption(option)} />
                            </div>
                        </div>

                        <div className={styles.configName}>
                            Configuration Name:
                            <div style={{ width: "200px" }}>
                                <EditableField isEditing={true} onChange={handleconfigChange} initialData={configName} />
                            </div>
                        </div>

                        <div className={styles.modalContent}>
                            <div className={styles.configCheckbox}>
                                {availableColumns.map((value, index) => (
                                    <div key={index} className={styles.checkboxItem}>
                                            <input type="checkbox"
                                            id={`checkbox${index}`}
                                            value={value}
                                            checked={draftSelectedColumns.includes(value)}
                                            onChange={() => handleCheckboxChange(value)} />
                                        <label htmlFor={`checkbox${index}`}>{columnsMap[value]}</label>
                                    </div>
                                ))}
                            </div>
                            <div className={styles.buttonGroup}>
                                <button className={styles.saveButton} onClick={handleSave}>
                                    Save
                                </button>
                                <button className={styles.applyButton} onClick={handleApply}>
                                    Apply
                                </button>
                            </div>
                        </div>
                    </DashboardConfiguration>
                </div>
            )}
        </div>
    )
}
